/**
 * The keys that work the table's selected row while its record shows below the
 * table: ↓ and ↑ move the selection, and Escape clears it.
 */

export type TableSelectionKeyAction = { kind: 'select'; rowId: string } | { kind: 'clear' } | null;

const TYPING_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/**
 * Whether the list should take this key at all. It leaves the key alone while
 * someone is typing, while a menu or dialog is open, and while a peek is open.
 * With a peek open, Escape is the peek's: it closes the peek and leaves the
 * selection as it was.
 */
export function listMayHandleKey(event: KeyboardEvent, peekIsOpen: boolean): boolean {
    if (event.defaultPrevented || peekIsOpen) return false;
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;
    const target = event.target;
    if (target instanceof Element && target.closest(TYPING_SELECTOR)) return false;
    // Reka marks every open menu, popover and dialog this way. Its Escape is that layer's.
    if (typeof document !== 'undefined' && document.querySelector('[data-dismissable-layer]')) return false;
    return true;
}

/**
 * What a key does to the selection. Nothing happens while no row is selected,
 * so ↑ and ↓ still scroll the table then. At the first or last row the key
 * does nothing: it does not wrap around or change the page.
 */
export function tableSelectionKeyAction(key: string, rowIds: string[], selectedRowId: string | null): TableSelectionKeyAction {
    if (selectedRowId === null) return null;
    if (key === 'Escape') return { kind: 'clear' };
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return null;
    const index = rowIds.indexOf(selectedRowId);
    if (index === -1) return null;
    const nextIndex = key === 'ArrowDown' ? index + 1 : index - 1;
    if (nextIndex < 0 || nextIndex >= rowIds.length) return null;
    return { kind: 'select', rowId: rowIds[nextIndex] };
}
